/** /reset-password?token=…（公開）— 以信中 token 設定新密碼；成功後導回登入。 */
import { useMemo, useState } from "react";
import { useLocation, useSearch } from "wouter";
import { ArrowRight, KeyRound } from "lucide-react";
import { trpc } from "../../lib/trpc";
import { useLanguage } from "../../contexts/LanguageContext";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { LogoImage } from "../../components/Logo";
import { ThemeToggle } from "../../components/theme-toggle";
import { PasswordFields, newPasswordValid } from "../../components/account/PasswordFields";
import { FormNotice } from "../../components/account/FormNotice";

export default function ResetPassword() {
  const [, setLocation] = useLocation();
  const search = useSearch();
  const token = useMemo(() => new URLSearchParams(search).get("token") ?? "", [search]);
  const { t } = useLanguage();
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const m = trpc.account.resetPassword.useMutation({
    onSuccess: () => setDone(true),
    onError: (e) => setError(e.message),
  });

  return (
    <div className="min-h-screen bg-background">
      <header className="flex items-center justify-between border-b border-border px-6 py-4 md:px-10">
        <button onClick={() => setLocation("/login")} className="transition-opacity hover:opacity-80">
          <LogoImage className="h-8" />
        </button>
        <ThemeToggle />
      </header>
      <main className="mx-auto max-w-md px-6 py-12">
        <div className="animate-fade-up space-y-6">
          <div className="space-y-2">
            <p className="font-mono text-[12px] uppercase tracking-[0.18em] text-primary">
              {t({ "zh-TW": "重設密碼", "en-US": "Reset password", "zh-CN": "重设密码" })}
            </p>
            <h1 className="font-display text-3xl font-medium tracking-tight">
              {t({ "zh-TW": "設定新密碼", "en-US": "Choose a new password", "zh-CN": "设定新密码" })}
            </h1>
          </div>
          <Card>
            <CardContent className="p-6">
              {token === "" ? (
                <div className="space-y-4">
                  <FormNotice kind="error">
                    {t({ "zh-TW": "連結缺少重設代碼，請從信件中的按鈕重新開啟，或重新申請。", "en-US": "This link is missing its reset code. Open it from the email again, or request a new one.", "zh-CN": "连结缺少重设代码，请从信件中的按钮重新开启，或重新申请。" })}
                  </FormNotice>
                  <Button variant="outline" className="w-full" onClick={() => setLocation("/forgot-password")}>
                    {t({ "zh-TW": "重新申請", "en-US": "Request again", "zh-CN": "重新申请" })}
                  </Button>
                </div>
              ) : done ? (
                <div className="space-y-4">
                  <FormNotice kind="success">
                    {t({ "zh-TW": "密碼已重設，請用新密碼登入。", "en-US": "Password reset. Sign in with your new password.", "zh-CN": "密码已重设，请用新密码登入。" })}
                  </FormNotice>
                  <Button className="w-full" size="lg" onClick={() => setLocation("/login")}>
                    {t({ "zh-TW": "前往登入", "en-US": "Go to sign in", "zh-CN": "前往登入" })}
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <form
                  className="space-y-4"
                  onSubmit={(e) => {
                    e.preventDefault();
                    setError(null);
                    m.mutate({ token, newPassword: pw });
                  }}
                >
                  <PasswordFields password={pw} confirm={confirm} onPassword={setPw} onConfirm={setConfirm} idPrefix="rp" autoFocus />
                  {error && (
                    <FormNotice kind="error">
                      {error}{" "}
                      <button type="button" className="underline" onClick={() => setLocation("/forgot-password")}>
                        {t({ "zh-TW": "重新申請", "en-US": "Request again", "zh-CN": "重新申请" })}
                      </button>
                    </FormNotice>
                  )}
                  <Button type="submit" className="w-full" size="lg" disabled={!newPasswordValid(pw, confirm) || m.isPending}>
                    <KeyRound className="h-4 w-4" />
                    {m.isPending ? t({ "zh-TW": "設定中…", "en-US": "Saving…", "zh-CN": "设定中…" }) : t({ "zh-TW": "設定新密碼", "en-US": "Set new password", "zh-CN": "设定新密码" })}
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
